import { useEffect, useRef, useState } from 'react';
import { insightChart } from './content';
import { useI18n } from '@/hooks/useI18n';
import styles from './InsightSection.module.css';

const RADIUS = 42;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Anima el arco de la dona la primera vez que la sección entra en pantalla.
 * Respeta `prefers-reduced-motion`: quien lo pidió ve el valor final de una vez,
 * sin transición.
 */
function useRevealOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || revealed) return;

    if (!('IntersectionObserver' in window)) {
      setRevealed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [revealed]);

  return { ref, revealed };
}

export function InsightSection() {
  const { ref, revealed } = useRevealOnce<HTMLDivElement>();
  const { t } = useI18n();
  const insight = t.marketing.insight;

  // Los porcentajes vienen de `content.ts`, no del diccionario: un 80/20 que
  // dijera 80/20 en español y 75/25 en inglés sería un defecto, no una
  // traducción.
  const { passiveLeadsPct, scannedLeadsPct } = insightChart;
  const passiveArc = (passiveLeadsPct / 100) * CIRCUMFERENCE;

  return (
    <section className={styles.section} id="insight">
      <div className={['ilu-container', styles.grid].join(' ')}>
        <div>
          <span className="ilu-eyebrow">{insight.eyebrow}</span>
          <h2 className={styles.headline}>{insight.headline}</h2>
          <p className={styles.question}>{insight.question}</p>
          <p className={styles.answer}>{insight.answer}</p>
        </div>

        <div className={styles.chart} ref={ref}>
          <div className={styles.donutWrap}>
            <svg viewBox="0 0 100 100" role="img" aria-label={insight.chartAlt(passiveLeadsPct)}>
              <circle
                cx="50"
                cy="50"
                r={RADIUS}
                fill="none"
                stroke="var(--ilu-surface-high)"
                strokeWidth="14"
              />
              <circle
                cx="50"
                cy="50"
                r={RADIUS}
                fill="none"
                stroke="var(--ilu-primary)"
                strokeWidth="14"
                strokeLinecap="round"
                strokeDasharray={`${revealed ? passiveArc : 0} ${CIRCUMFERENCE}`}
                transform="rotate(-90 50 50)"
                style={{ transition: 'stroke-dasharray 1.8s cubic-bezier(0.16, 1, 0.3, 1)' }}
              />
            </svg>
            <div className={styles.donutValue} aria-hidden="true">
              <strong>{passiveLeadsPct}%</strong>
              <span>{insight.donutLabel}</span>
            </div>
          </div>

          <ul className={styles.legend}>
            <li className={styles.legendItem}>
              <span className={styles.swatch} style={{ background: 'var(--ilu-primary)' }} />
              {insight.legendPassive(passiveLeadsPct)}
            </li>
            <li className={styles.legendItem}>
              <span className={styles.swatch} style={{ background: 'var(--ilu-surface-high)' }} />
              {insight.legendScanned(scannedLeadsPct)}
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
